/**
 * TextStack Component
 *
 * Displays a primary text with optional secondary metadata below.
 * Perfect for table cells, list items, or anywhere you need
 * a title with supporting details.
 *
 * @example
 * ```jsx
 * // Basic usage
 * <TextStack
 *   primary="Project Alpha"
 *   secondary="Engineering · 3 members"
 * />
 *
 * // With array of metadata items (auto-joined with separator)
 * <TextStack
 *   primary={ticket.subject}
 *   secondary={[ticket.type, getUserFullName(ticket.assignedTo)]}
 * />
 *
 * // With custom separator
 * <TextStack
 *   primary="John Doe"
 *   secondary={["Admin", "Active"]}
 *   separator=" | "
 * />
 *
 * // With fallback for empty primary
 * <TextStack
 *   primary={item.name}
 *   primaryFallback="Untitled"
 *   secondary={item.description}
 * />
 *
 * // Different sizes
 * <TextStack size="sm" primary="Small" secondary="Details" />
 * <TextStack size="md" primary="Medium" secondary="Details" />
 * <TextStack size="lg" primary="Large" secondary="Details" />
 * ```
 */

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const SIZE_CONFIG = {
	xs: {
		primary: 'text-xs',
		secondary: 'text-[10px]',
		gap: 'mt-0.5',
	},
	sm: {
		primary: 'text-sm',
		secondary: 'text-xs',
		gap: 'mt-0.5',
	},
	md: {
		primary: 'text-base',
		secondary: 'text-sm',
		gap: 'mt-1',
	},
	lg: {
		primary: 'text-lg',
		secondary: 'text-base',
		gap: 'mt-1',
	},
};

const TextStack = ({
	// Content
	primary,
	primaryFallback = null,
	secondary,
	secondaryFallback = null,

	// Formatting
	separator = ' · ',
	size = 'sm',

	// Styling
	primaryClassName = '',
	secondaryClassName = '',
	className = '',

	// Options
	capitalize = false, // Capitalize items in secondary array
	truncate = false, // Truncate primary text
	inline = false, // Display primary and secondary on the same line
}) => {
	const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.sm;

	// Process primary text
	const displayPrimary = primary || primaryFallback;

	// Process secondary - can be string, array, or ReactNode
	const renderSecondary = () => {
		if (!secondary && !secondaryFallback) return null;

		// If it's an array, filter out falsy values and join with separator
		if (Array.isArray(secondary)) {
			const filteredItems = secondary.filter(Boolean);
			if (filteredItems.length === 0) {
				return secondaryFallback;
			}

			return filteredItems.map((item, index) => (
				<span key={index} className={capitalize ? 'capitalize' : ''}>
					{index > 0 && <span className="text-gray-300">{separator}</span>}
					{item}
				</span>
			));
		}

		// Otherwise just return the secondary content
		return secondary || secondaryFallback;
	};

	const secondaryContent = renderSecondary();

	return (
		<div className={classNames(inline ? 'flex items-center gap-2' : '', className)}>
			{displayPrimary && (
				<p
					className={classNames(
						'text-gray-900 font-medium',
						sizeConfig.primary,
						truncate ? 'truncate' : '',
						primaryClassName,
					)}
				>
					{displayPrimary}
				</p>
			)}
			{secondaryContent && (
				<p
					className={classNames(
						'text-gray-400',
						sizeConfig.secondary,
						inline ? '' : sizeConfig.gap,
						secondaryClassName,
					)}
				>
					{secondaryContent}
				</p>
			)}
		</div>
	);
};

export default TextStack;
